import { AfterViewChecked, Component, effect, ElementRef, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TextFieldModule } from '@angular/cdk/text-field';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { StingrayAuthenticationService } from '@app/core/authentication/stingray-authentication.service';
import { ChatMessage, ChatService, PendingWorkflowRun } from './chat.service';
import { MarkdownPipe } from './markdown.pipe';

@Component({
  selector: 'unite-chat-window',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatBadgeModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatTooltipModule,
    TextFieldModule,
    RouterLink,
    TranslateModule,
    MarkdownPipe,
  ],
  templateUrl: './chat-window.component.html',
  styleUrl: './chat-window.component.scss',
})
export class ChatWindowComponent implements AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef<HTMLElement>;
  @ViewChild('fileInput') private fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('messageInput') private messageInput!: ElementRef<HTMLTextAreaElement>;
  @Output() closeClicked = new EventEmitter<void>();

  protected chatService = inject(ChatService);
  private authService = inject(StingrayAuthenticationService);
  private router = inject(Router);
  protected inputText = '';
  private pendingScrollToBottom = false;

  constructor() {
    // The list is only fetched when the history panel opens; a turn that completes while the panel is
    // open (a new conversation, or a retitled one) would otherwise not show until it is reopened.
    let wasStreaming = false;
    effect(() => {
      const streaming = this.chatService.isStreaming();
      if (wasStreaming && !streaming && this.showHistory) {
        this.chatService.loadConversations();
      }
      wasStreaming = streaming;
    });

    // Following an upload the assistant works on: each refreshed step scrolls into view.
    effect(() => {
      this.chatService.messages();
      if (this.chatService.activeRunWorking()) this.pendingScrollToBottom = true;
    });
  }

  get firstName(): string {
    return this.authService.user?.firstName ?? '';
  }

  ngAfterViewChecked(): void {
    if (this.chatService.isStreaming() || this.pendingScrollToBottom) {
      this.scrollToBottom();
      this.pendingScrollToBottom = false;
    }
  }

  protected onEnterKey(event: Event): void {
    if ((event as KeyboardEvent).shiftKey) return;
    event.preventDefault();
    this.sendMessage();
  }

  protected sendMessage(): void {
    const text = this.inputText.trim();
    if (!text || this.chatService.isStreaming()) return;
    this.inputText = '';
    this.chatService.sendMessage(text);
  }

  /** "Other" answer to a pending question: dismiss the card and focus the input so the user types their own reply. */
  protected answerOther(): void {
    this.chatService.dismissQuestion();
    setTimeout(() => this.messageInput?.nativeElement.focus());
  }

  protected attachFile(): void {
    this.fileInput.nativeElement.click();
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.chatService.uploadFile(file);
    }
    input.value = '';
  }

  protected onMessageBubbleClick(event: MouseEvent): void {
    const anchor = (event.target as Element).closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href') ?? '';
    const target = this.chatService.resolveAppLink(href);
    if (target) {
      event.preventDefault();
      this.router.navigate(target.commands, { queryParams: target.queryParams });
      return;
    }
    // An unresolved app:// link must never reach the browser/OS: there is no
    // registered handler for the scheme, so opening it throws a launch error.
    if (/^(unsafe:)?app:/i.test(href)) {
      event.preventDefault();
      console.warn('Unresolved app link ignored:', href);
      return;
    }
    event.preventDefault();
    window.open(href, '_blank', 'noopener,noreferrer');
  }

  protected newConversation(): void {
    this.chatService.clearConversation();
    this.oauthWindowOpened = false;
    this.chatService.showHistory.set(false);
    this.pendingScrollToBottom = true;
  }

  /** Lives in the service, so opening a pending run from the header also leaves the history panel. */
  protected get showHistory(): boolean {
    return this.chatService.showHistory();
  }

  protected toggleHistory(): void {
    this.chatService.showHistory.set(!this.showHistory);
    if (this.showHistory) {
      this.chatService.loadConversations();
    } else {
      this.pendingScrollToBottom = true;
    }
  }

  protected selectConversation(id: string): void {
    this.chatService.loadConversation(id);
    this.oauthWindowOpened = false;
    this.pendingScrollToBottom = true;
  }

  /** Shows the documents waiting for an answer, listed at the top of the recent-conversations panel. */
  protected showPendingRuns(): void {
    this.chatService.showHistory.set(true);
    this.chatService.loadConversations();
  }

  /** Opens a document the assistant has prepared, with its open question ready to answer. */
  protected openPendingRun(run: PendingWorkflowRun): void {
    this.chatService.openPendingRun(run);
    this.oauthWindowOpened = false;
    this.pendingScrollToBottom = true;
  }

  protected deleteConversation(event: Event, id: string): void {
    event.stopPropagation();
    this.chatService.deleteConversation(id);
  }

  protected oauthWindowOpened = false;

  protected openOAuthWindow(url: string): void {
    window.open(url, '_blank', 'noopener,noreferrer');
    this.oauthWindowOpened = true;
  }

  protected continueAfterOAuth(): void {
    this.oauthWindowOpened = false;
    this.chatService.submitOAuthConsent(true);
  }

  protected showAssistantBubble(message: ChatMessage): boolean {
    if (!message.content) return !message.isStreaming;
    return !(message.isStreaming && message.content.startsWith('{'));
  }

  protected get showTypingIndicator(): boolean {
    // Show the thinking indicator for the whole run. A single run streams multiple bubbles
    // with pauses in between (text arrives, then the model works on the next step before
    // TEXT_MESSAGE_END), so the only reliable "still working" signal is the run itself.
    // Pending question/approval/oauth all clear isStreaming, so this won't collide with them.
    // An upload the assistant works on in the background shows it too, while the conversation is followed.
    return this.chatService.isStreaming() || this.chatService.activeRunWorking();
  }

  private scrollToBottom(): void {
    if (!this.messagesContainer) return;
    const el = this.messagesContainer.nativeElement;
    el.scrollTop = el.scrollHeight;
  }
}
